
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { WelcomeBanner } from "@/components/WelcomeBanner";
import { Project } from "@/types/Project";
import { useProjects } from "@/hooks/useProjects";
import { ProjectsHeader } from "@/components/projects/ProjectsHeader";
import { ProjectTabs } from "@/components/projects/ProjectTabs";
import { TimeEntry } from "@/components/TimeEntryForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export default function Projects() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { 
    projects, 
    isLoading, 
    isNewUser, 
    activeTab, 
    setActiveTab,
    createProject,
    archiveProject
  } = useProjects();

  const handleAddProject = async (project: any) => {
    // The project data is already properly formatted from the form
    // Just update the UI state since the createProject mutation handles the database
    console.log('Project form completed successfully:', project);
    // No need to call createProject again - it was already called in the form
  };

  const handleArchiveProject = (id: string) => {
    archiveProject(id);
  };

  const handleSelectProject = (project: Project) => {
    navigate(`/projects/${project.id}`);
  };

  const handleCreateNewProject = () => {
    setActiveTab("new");
  };

  const addEntryMutation = useMutation({
    mutationFn: async (entry: TimeEntry) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('time_entries')
        .insert({
          user_id: user.user.id,
          project_id: entry.projectId,
          description: entry.description,
          minutes: entry.minutes,
          date: entry.date.toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    }
  });

  const handleAddTimeEntry = async (entry: TimeEntry) => {
    try {
      await addEntryMutation.mutateAsync(entry);
      const project = projects?.find(p => p.id === entry.projectId);
      toast({
        title: "Time Entry Added",
        description: `Added ${Math.floor(entry.minutes / 60)}h ${entry.minutes % 60}m to ${project?.name || 'project'}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add time entry. Please try again.",
        variant: "destructive"
      });
    }
  };

  const activeProjects = projects?.filter(p => !p.isArchived) || [];
  const hasProjects = activeProjects.length > 0;

  return (
    <>
      <Navigation />
      <div className="container py-8 space-y-6 max-w-5xl mt-14">
        <ProjectsHeader 
          hasProjects={hasProjects} 
          onCreateNewProject={handleCreateNewProject}
          projects={projects || []}
          onAddTimeEntry={handleAddTimeEntry}
        />

        {isNewUser && (
          <WelcomeBanner
            title="Welcome to Hour Tracker!"
            description="Let's get started by creating your first project."
            ctaLabel="Create Your First Project"
            onCtaClick={() => setActiveTab("new")}
          />
        )}

        <div className="grid md:grid-cols-[1fr_300px] gap-6">
          <div>
            <ProjectTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              projects={projects || []}
              isLoading={isLoading}
              handleAddProject={handleAddProject}
              handleArchiveProject={handleArchiveProject}
              handleSelectProject={handleSelectProject}
              handleCreateNewProject={handleCreateNewProject}
            />
          </div>
        </div>
      </div>
    </>
  );
}
